// Subtle brand watermark for share-out images. Keep it small and bottom-
// anchored so users don't crop it out before posting.
//
// Two lines:
//   "<Look name> · beautyiq.cloud"    (style identity = SEO-friendly micro-ad)
//   "<brand> · <productName>" OR tagline  (specific product if user picked one)
//
// We deliberately skip a solid pill/background: the multiply of text over
// the already-composited photo reads as "signed" not "branded ad" — higher
// share-through rate in informal testing of similar overlays.

import type { MakeupStyle, ProductMatch } from '../catalog/catalog';
import { capitaliseBrand } from '../catalog/catalog';

const TOP_LINE = 'BeautyIQ · beautyiq.cloud';

function pickTextColor(luminance: number): string {
  // Watermark sits on the bottom edge. If the image is dark there, use
  // near-white with low alpha; if bright, use near-black.
  return luminance < 0.45 ? 'rgba(255, 255, 255, 0.88)' : 'rgba(20, 18, 15, 0.82)';
}

function sampleFooterLuminance(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number
): number {
  const stripH = Math.max(2, Math.floor(height * 0.1));
  const data = ctx.getImageData(0, height - stripH, width, stripH).data;
  let total = 0;
  const step = 4 * 8; // sample every 8th pixel for speed
  let n = 0;
  for (let i = 0; i < data.length; i += step) {
    const r = data[i] ?? 0;
    const g = data[i + 1] ?? 0;
    const b = data[i + 2] ?? 0;
    // Rec. 709 luminance
    total += (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    n++;
  }
  return n > 0 ? total / n : 0.5;
}

export function applyWatermark(
  base: HTMLCanvasElement,
  style: MakeupStyle,
  activeMatch: ProductMatch | null
): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = base.width;
  out.height = base.height;
  const ctx = out.getContext('2d');
  if (!ctx) return base;

  ctx.drawImage(base, 0, 0);

  const lum = sampleFooterLuminance(ctx, out.width, out.height);
  const color = pickTextColor(lum);

  // Scale font to image width so the watermark looks consistent regardless
  // of source resolution.
  const fontScale = Math.max(out.width, out.height) / 1080;
  const brandSize = Math.round(24 * fontScale);
  const shadeSize = Math.round(18 * fontScale);
  const margin = Math.round(28 * fontScale);

  ctx.save();
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = color;

  // Top line: style name (the "look" identity the user picked).
  ctx.font = `600 ${brandSize}px "Inter", system-ui, sans-serif`;
  const topY = out.height - margin - shadeSize - 8 * fontScale;
  ctx.fillText(`${style.name} · beautyiq.cloud`, margin, topY);

  // Bottom line: either the specific product the user chose from shop-the-look,
  // or a softer "look curated by BeautyIQ" signature when no match is selected.
  ctx.font = `400 ${shadeSize}px "Inter", system-ui, sans-serif`;
  const bottomLine = activeMatch
    ? `${capitaliseBrand(activeMatch.brand)} · ${activeMatch.productName}`
    : style.tagline;
  ctx.fillText(bottomLine, margin, out.height - margin);

  ctx.restore();
  return out;
}

export async function canvasToBlob(
  canvas: HTMLCanvasElement,
  quality = 0.92
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob failed'))),
      'image/jpeg',
      quality
    );
  });
}
